import { Edge, EdgeView, Graph, Point, Node, Cell } from "@antv/x6"
import { FlowsheetContent, LineEndEnum, LineRecord, NodeState, NodeType, SvgStreamDataState } from "./services/modal"
import { EquipmentType, NozzleDirection } from "@/types/common"
import { Equipment } from "@/services/project/equipment/equipment.model"
import { v4 as uuidv4 } from "uuid";
import { EventBus } from "@/libs/EventBus"
import { addNozzleToNozzleEdge } from "./services/NozzleEdgeLayout"
import isEmpty from "lodash/isEmpty";
import { isArray, isObject, uniqBy, cloneDeep, isEqual } from "lodash";
import { CustomCellType } from "../Spreadsheet/VariableCell"
import { LineType, PapersizeOptions } from "./constant"
import { PaperDirection, PaperSize } from "@/store/flowsheet/flowsheet.slice"

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })

export const getGraphSvg = (graph: Graph): Promise<string> => {
  return new Promise((resolve) => {
    graph.toSVG((content) => {
      resolve(content)
    }, {
      copyStyles: false,
    })
  })
}

export const COLUMNS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']

export const dataToSimFlow = (data: FlowsheetContent) => {
  const {sheets, setOperations, spreadsheets} = data
  const allNodes = sheets.flatMap(item => item.nodes).filter(item => item.nodeType === NodeType.SvgNode)
  const allStreams = sheets.flatMap(item => item.lines).filter(item => item.type === LineType.FlowLine)
  const nodeUnits = allNodes
    .map(node => {
      const args: any = {...(node.simArgs ?? {})}
      return {
        type: node.equipment.type,
        tag: node.tag,
        uuid: node.id,
        args
      }
    })
  const lineUnits = uniqBy(allStreams.map(line => {
    return {
      type: EquipmentType.Stream,
      uuid: line.streamId || line.id,
      tag: line.tag
    }
  }), (item) => {
    return item.uuid;
  })

  const setUnits = setOperations.map(item => {
    return {
      type: EquipmentType.Set,
      uuid: item.id,
      tag: item.tag,
      avars: {
        'INPUT:INPUT': `${item.sourceUnit}${item.sourceVar}`,
        'OUTPUT:OUTPUT': `${item.targetUnit}${item.targetVar}`,
      }
    }
  })

  const spreadsheetUnits = spreadsheets.flatMap(workbook => {
    if (!workbook.data) return;
    const sheetData = workbook.data;
    if (!isArray(sheetData)) {
      return;
    }
    const unitData = {
      type: EquipmentType.Spreadsheet,
      uuid: workbook.id,
      tag: workbook.tag,
      avars: {},
      args: {}
    }
    sheetData.forEach((row, rowIndex) => {
      row.forEach((cellVal, colIndex) => {
        if (!isObject(cellVal)) {
          if (cellVal && !isNaN(Number(cellVal))) {
            const cellX = colIndex;
            const cellY = rowIndex + 1;
            const key = `${COLUMNS[Number(cellX)]}${cellY}`
            unitData.args[key] = Number(cellVal)
          }
          return
        }
        const cellX = colIndex;
        const cellY = rowIndex + 1;
        if (cellVal?.t === CustomCellType.Variable) {
          const {unitId, attrCode} = cellVal;
          const key = `${COLUMNS[Number(cellX)]}${cellY}`
          unitData.avars[key] = `${unitId}${attrCode}`
        } else if (cellVal?.t === CustomCellType.Formula && cellVal?.formula) {
          const val = cellVal.formula.replace(/^=/, '')
          if (!val) return;
          const key = `${COLUMNS[Number(cellX)]}${cellY}`
          unitData.args[key] = val
        } else if (cellVal?.t === CustomCellType.VariableFormula) {
          const {unitId, attrCode} = cellVal;
          const key = `${COLUMNS[Number(cellX)]}${cellY}`
          unitData.avars[key] = `${unitId}${attrCode}`
          if (cellVal?.formula) {
            const val = cellVal.formula.replace(/^=/, '')
            if (!val) return;
            unitData.args[key] = val
          }
        }
      })
    });
    if (isEmpty(unitData.avars)) {
      return
    }
    return unitData;
  }).filter(item => !!item)

  const units = [...nodeUnits, ...lineUnits, ...setUnits, ...spreadsheetUnits]
  
  const connections = allStreams.flatMap((line) => {
    const {cell: srcCell, port: srcPort} = line.source as any || {}
    const {cell: targetCell, port: targetPort} = line.target as any || {}
    const res: any = []
    if (srcCell && srcPort) {
      res.push(getConnection(srcCell, srcPort, line, allNodes))
    }

    if (targetCell && targetPort) {
      res.push(getConnection(targetCell, targetPort, line, allNodes))
    }
    return res;
  }).filter(item => {
    return item
  }).sort((a, b) => {
    if (a.inletUnit > b.inletUnit) {
      return 1
    } else if (a.inletUnit === b.inletUnit) {
      return a.outletUnit > b.outletUnit ? 1 : -1
    }
    return -1
  })
  return {
    units,
    connections
  }
}

function findNozzleFromNode(node: NodeState, portId: string) {
  const defaultNozzle = node.nozzles.find(item => item.id === portId)
  if (defaultNozzle) {
    return defaultNozzle
  }
  return (node.customNozzles || []).find(item => item.id === portId)
}

function getConnection(cell: string, port: string, line: LineRecord, nodes: NodeState[]): {
  inletUnit: string
  inletPort: string
  outletUnit: string
  outletPort: string
} | undefined {
  const node = nodes.find(item => item.id === cell)
  if (!node) return;
  const nozzle = findNozzleFromNode(node, port)
  if (!nozzle) return;
  const lineId = line.streamId || line.id
  let inletUnit = node.id
  let outletUnit = lineId
  let inletPort = nozzle.name
  let outletPort = 'OUT'
  if (nozzle.direction === NozzleDirection.Outlet) {
    inletUnit = lineId
    outletUnit = node.id
    inletPort = 'IN'
    outletPort = nozzle.name
  }
  
  return {
    inletUnit,
    outletUnit,
    inletPort,
    outletPort,
  } as any
}

export function getLineEndType(end: any) {
  if (end.cell && end.port) {
    return LineEndEnum.LinkToPort
  } else if (end.cell) {
    return LineEndEnum.LinkToEdge
  }
  return LineEndEnum.Point
}

export function getLineCenter(edge: Edge, graph: Graph): Point | null {
  if (!edge) return null;
  const edgeView = <EdgeView>edge.findView(graph)
  if (edgeView) {
    return edgeView.getPointAtRatio(0.5)
  }
  return null
}

export async function createNozzleToNode(
  nozzleEdge: Equipment.NozzleEdge,
  node: Node,
  graph: Graph
): Promise<string> {
  const newNozzleId = uuidv4()
  addNozzleToNozzleEdge(newNozzleId, nozzleEdge, node, graph)

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(newNozzleId)
    }, 200)
  })
}

export async function createNozzleToNodeAndConnect(
  graph: Graph,
  nozzleEdge: Equipment.NozzleEdge,
  node: Node,
  eventBus: EventBus,
  edge: Edge,
  endType: 'target' | 'source'
) {
  const newNozzleId = await createNozzleToNode(nozzleEdge, node, graph)

  eventBus.trigger('node:nozzle:changed', {node, type: 'add'})
  if (endType === 'target') {
    edge.setTarget({cell: node.id, port: newNozzleId})
  } else {
    edge.setSource({cell: node.id, port: newNozzleId})
  }
}


export function closestPointOnSegment(start, end, z) {
    // 计算向量 S 和 P
    const S = { x: end.x - start.x, y: end.y - start.y };
    const P = { x: z.x - start.x, y: z.y - start.y };

    // 计算点积
    const S_dot_S = S.x * S.x + S.y * S.y;
    const P_dot_S = P.x * S.x + P.y * S.y;

    // 投影系数 t
    const t = P_dot_S / S_dot_S;

    // 根据 t 的值判断最近点的位置
    if (t < 0) {
        return start;
    } else if (t > 1) {
        return end;
    } else {
        return {
            x: start.x + t * S.x,
            y: start.y + t * S.y
        };
    }
}

export function removeTemplateCells(cells: Cell.Properties[]) {
  return cells.filter(item => !(item.shape === 'edge' && item.data.type === LineType.Template))
}

export function timeoutToPromise(callback: () => Promise<any> | void, timeout: number) {
  return new Promise((resolve) => {
    setTimeout(async () => {
      const res = await callback()
      resolve(res)
    }, timeout);
  })
}

export function intervalToPromise(callback: () => Promise<any> | void, timeout: number) {
  return new Promise((resolve) => {
    const timer =setInterval(async () => {
      const res = await callback()
      if (res) {
        clearInterval(timer)
        resolve(res)
      }
    }, timeout);
  })
}

export function pointRotate(point: PositionLike, center: PositionLike, angle = 0) {
  const { x, y } = point
  const { x: cx, y: cy } = center
  const radians = (Math.PI / 180) * angle,
    cos = Math.cos(radians),
    sin = Math.sin(radians),
    nx = cos * (x - cx) + sin * (y - cy) + cx,
    ny = cos * (y - cy) + sin * (x - cx) + cy

  return {
    x: nx,
    y: ny
  }
}

export function isLineValid(line: Cell.Properties) {
  if (line.shape !== 'edge') return false;
  if (isEqual(line.source, line.target) && line.vertices.length === 0) {
    return false;
  }
  return true
}

export function centerNode(graph: Graph, node: Node) {
  graph.centerCell(node);
  const view = graph.findViewByCell(node)
  if (view) {
    view.container.classList.add('svg-shake')
    setTimeout(() => {
      view.container.classList.remove('svg-shake')
    }, 500)
  }
}

export function getParperWidth(size: PaperSize, direction: PaperDirection): number {
  const target = PapersizeOptions.find(item => item.value === size)!
  if (direction === PaperDirection.Portrait) {
    return target.height
  }
  return target.width
}

export function getParperSize(size: PaperSize, direction: PaperDirection): {
  width: number, height: number
} {
  const target = PapersizeOptions.find(item => item.value === size)!
  if (direction === PaperDirection.Portrait) {
    return {
      width: target.height,
      height: target.width
    }
  }
  return {
    width: target.width,
    height: target.height
  }
}